/**
 * SessionTranscriptTab
 *
 * In-session audio recording and real-time transcript panel.
 * Allows any participant to start a recording; transcript segments are
 * attributed to the recording participant and shared with the whole session
 * via Supabase Realtime.
 */

import { useEffect, useCallback, useRef, useState } from 'react';
import {
  Mic,
  MicOff,
  Pause,
  Play,
  Square,
  Clock,
  User,
  AlertCircle,
  ChevronDown,
  ChevronUp,
  Volume2,
} from 'lucide-react';
import type { PrioritizationSession } from '../../../types/prioritization/session';
import type { SessionRecording } from '../../../types/prioritization/sessionRecording';
import { useSessionRecordingStore } from '../../../stores/prioritization/sessionRecordingStore';
import { useSessionRecorder } from '../../../hooks/useSessionRecorder';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { formatOffsetClock } from '../../../lib/sessionTranscript';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatTimestamp(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function formatDate(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function speakerInitials(label: string): string {
  return label
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

// Stable hue per speaker label so colors are consistent within a session
const SPEAKER_COLORS = [
  'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  'bg-[var(--green-dim)] text-[var(--green)] dark:text-[var(--text-ter)]',
  'bg-[var(--mustard-dim)] text-[var(--mustard)]',
  'bg-[var(--coral-dim)] text-[var(--coral)]',
  'bg-[var(--slate-dim)] text-[var(--text-sec)]',
  'bg-cyan-100 text-cyan-700',
];

function speakerColor(label: string): string {
  let hash = 0;
  for (let i = 0; i < label.length; i++) {
    hash = (hash * 31 + label.charCodeAt(i)) >>> 0;
  }
  return SPEAKER_COLORS[hash % SPEAKER_COLORS.length];
}

// ─── Sub-components ───────────────────────────────────────────────────────────

interface RecordingBadgeProps {
  status: SessionRecording['status'];
}

function RecordingBadge({ status }: RecordingBadgeProps) {
  const cfg: Record<
    SessionRecording['status'],
    { label: string; dot: string; text: string }
  > = {
    recording: {
      label: 'Recording',
      dot: 'bg-[var(--coral)] animate-pulse',
      text: 'text-[var(--coral)]',
    },
    paused: {
      label: 'Paused',
      dot: 'bg-[var(--mustard)]',
      text: 'text-[var(--mustard)]',
    },
    complete: {
      label: 'Complete',
      dot: 'bg-[var(--green)]',
      text: 'text-[var(--green)] dark:text-[var(--text-ter)]',
    },
    failed: {
      label: 'Failed',
      dot: 'bg-[var(--surface)]',
      text: 'text-[var(--text-sec)]',
    },
  };

  const { label, dot, text } = cfg[status];

  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${text}`}>
      <span className={`h-2 w-2 rounded-full flex-shrink-0 ${dot}`} />
      {label}
    </span>
  );
}

// ─── Past recording row ───────────────────────────────────────────────────────

interface PastRecordingRowProps {
  recording: SessionRecording;
  isExpanded: boolean;
  onToggle(): void;
  onSelect(): void;
  isActive: boolean;
}

function PastRecordingRow({
  recording,
  isExpanded,
  onToggle,
  onSelect,
  isActive,
}: PastRecordingRowProps) {
  return (
    <div
      className={`border  overflow-hidden transition-colors ${
        isActive ? 'border-[var(--slate)] bg-[var(--slate-dim)]/30' : 'border-[var(--rule)] bg-[var(--bg-raised)]'
      }`}
    >
      <button
        type="button"
        onClick={() => {
          onSelect();
          onToggle();
        }}
        className="w-full flex items-center justify-between px-4 py-3 text-sm text-left hover:bg-[var(--surface)]/80 transition-colors"
      >
        <div className="flex items-center gap-3 min-w-0">
          <Volume2 className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
          <span className="font-medium text-[var(--text-sec)] truncate">
            {formatDate(recording.startedAt)}
          </span>
          <span className="text-[var(--text-ter)]">·</span>
          <span className="text-[var(--text-ter)]">
            {formatTimestamp(recording.startedAt)}
          </span>
          {recording.durationMs !== undefined && (
            <>
              <span className="text-[var(--text-ter)]">·</span>
              <span className="text-[var(--text-ter)] flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatOffsetClock(recording.durationMs)}
              </span>
            </>
          )}
          <RecordingBadge status={recording.status} />
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
        )}
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

interface Props {
  session: PrioritizationSession;
}

export function SessionTranscriptTab({ session }: Props) {
  const userId = useUserStore((s) => s.userId);
  const userName = useUserStore((s) => s.userName || s.userEmail?.split('@')[0] || 'Unknown');
  const workspaceId = session.workspaceId ?? '';

  const {
    recordings,
    activeRecordingId,
    segments,
    isLoading,
    loadRecordings,
    loadSegments,
    startRecording,
    pauseRecording,
    resumeRecording,
    completeRecording,
    failRecording: _failRecording,
    appendSegment,
    subscribeToSegments,
    unsubscribe,
    reset,
  } = useSessionRecordingStore();

  const [selectedRecordingId, setSelectedRecordingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [permissionDenied, setPermissionDenied] = useState(false);
  const [interimText, setInterimText] = useState('');

  const transcriptEndRef = useRef<HTMLDivElement>(null);
  const recordingStartMs = useRef<number>(0);

  // ── Recording hook ────────────────────────────────────────────────────────

  const handleFinalSegment = useCallback(
    async (seg: { text: string; startMs: number; endMs: number; confidence: number }) => {
      const recId = useSessionRecordingStore.getState().activeRecordingId;
      if (!recId) return;

      await appendSegment({
        recordingId: recId,
        sessionId: session.id,
        workspaceId,
        speakerUserId: userId,
        speakerLabel: userName,
        text: seg.text,
        startMs: seg.startMs,
        endMs: seg.endMs,
        isFinal: true,
        confidence: seg.confidence,
      });
    },
    [session.id, workspaceId, userId, userName, appendSegment],
  );

  const recorder = useSessionRecorder({
    onFinalSegment: handleFinalSegment,
    onInterimText: setInterimText,
  });

  // ── Lifecycle ─────────────────────────────────────────────────────────────

  useEffect(() => {
    reset();
    loadRecordings(session.id);
    subscribeToSegments(session.id);
    return () => {
      unsubscribe();
    };
  }, [session.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-select most recent recording and load its segments
  useEffect(() => {
    if (recordings.length === 0) return;
    const target = activeRecordingId ?? recordings[0]?.id;
    if (target && target !== selectedRecordingId) {
      setSelectedRecordingId(target);
      loadSegments(target);
      setExpandedIds((prev) => new Set([...prev, target]));
    }
  }, [recordings, activeRecordingId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to the bottom of the transcript as segments arrive
  useEffect(() => {
    transcriptEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [segments, interimText]);

  // ── Controls ──────────────────────────────────────────────────────────────

  const handleStart = useCallback(async () => {
    setPermissionDenied(false);

    const granted = await recorder.requestPermission();
    if (!granted) {
      setPermissionDenied(true);
      return;
    }

    const recId = await startRecording(session.id, workspaceId, userId);
    if (!recId) return;

    recordingStartMs.current = Date.now();
    await recorder.start();
    setSelectedRecordingId(recId);
    setExpandedIds((prev) => new Set([...prev, recId]));
  }, [recorder, session.id, workspaceId, userId, startRecording]);

  const handlePause = useCallback(async () => {
    recorder.pause();
    if (activeRecordingId) await pauseRecording(activeRecordingId);
  }, [recorder, activeRecordingId, pauseRecording]);

  const handleResume = useCallback(async () => {
    recorder.resume();
    if (activeRecordingId) await resumeRecording(activeRecordingId);
  }, [recorder, activeRecordingId, resumeRecording]);

  const handleStop = useCallback(async () => {
    const totalMs = recorder.stop();
    if (activeRecordingId) {
      await completeRecording(activeRecordingId, totalMs);
    }
    setInterimText('');
  }, [recorder, activeRecordingId, completeRecording]);

  const handleSelectRecording = useCallback(
    (id: string) => {
      setSelectedRecordingId(id);
      loadSegments(id);
    },
    [loadSegments],
  );

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // ── Derived ───────────────────────────────────────────────────────────────

  const isRecording = recorder.recorderState === 'recording';
  const isPaused = recorder.recorderState === 'paused';
  const hasActiveSession = isRecording || isPaused;

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--text-pri)]">Session Transcript</h2>
          <p className="mt-0.5 text-sm text-[var(--text-ter)]">
            Record the discussion and capture a live transcript attributed to each
            participant.
          </p>
        </div>

        {/* Controls */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!hasActiveSession && (
            <button
              type="button"
              onClick={handleStart}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-[var(--coral)] hover:bg-[var(--coral)] transition-colors"
            >
              <Mic className="h-4 w-4" />
              Start Recording
            </button>
          )}

          {isRecording && (
            <>
              <span className="inline-flex items-center gap-1.5 text-sm font-mono text-[var(--coral)]">
                <span className="h-2 w-2 rounded-full bg-[var(--coral)] animate-pulse" />
                {formatOffsetClock(recorder.elapsedMs)}
              </span>
              <button
                type="button"
                onClick={handlePause}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
              >
                <Pause className="h-4 w-4" />
                Pause
              </button>
              <button
                type="button"
                onClick={handleStop}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
              >
                <Square className="h-4 w-4" />
                Stop
              </button>
            </>
          )}

          {isPaused && (
            <>
              <span className="inline-flex items-center gap-1.5 text-sm font-mono text-[var(--mustard)]">
                <span className="h-2 w-2 rounded-full bg-[var(--mustard)]" />
                {formatOffsetClock(recorder.elapsedMs)} (paused)
              </span>
              <button
                type="button"
                onClick={handleResume}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-white bg-[var(--slate)] hover:bg-[var(--slate)] transition-colors"
              >
                <Play className="h-4 w-4" />
                Resume
              </button>
              <button
                type="button"
                onClick={handleStop}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] bg-[var(--bg-raised)] border border-[var(--rule)] hover:bg-[var(--surface)] transition-colors"
              >
                <Square className="h-4 w-4" />
                Stop
              </button>
            </>
          )}
        </div>
      </div>

      {/* ── Permission denied notice ── */}
      {permissionDenied && (
        <div className="flex items-start gap-3 p-3 bg-[var(--coral-dim)] border border-[var(--coral)]">
          <AlertCircle className="h-4 w-4 text-[var(--coral)] flex-shrink-0 mt-0.5" />
          <p className="text-sm text-[var(--coral)]">
            Microphone access was denied. Allow microphone access in your browser
            settings and try again.
          </p>
        </div>
      )}

      {/* ── No Speech Recognition notice ── */}
      {!recorder.isTranscriptionSupported && (
        <div className="flex items-start gap-3 p-3 bg-[var(--mustard-dim)] border border-[var(--mustard)]">
          <AlertCircle className="h-4 w-4 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
          <div className="text-sm text-[var(--mustard)]">
            <p className="font-medium">Live transcription not supported in this browser.</p>
            <p className="mt-0.5">
              Use Chrome or Edge for real-time speech-to-text. Recording controls are
              still available and transcripts from other participants will appear here.
            </p>
          </div>
        </div>
      )}

      {/* ── Live transcript (active recording) ── */}
      {hasActiveSession && (
        <div className="bg-[var(--bg-raised)] border border-[var(--rule)] overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--rule)]">
            <span className="text-sm font-medium text-[var(--text-sec)]">Live transcript</span>
            <RecordingBadge status={isRecording ? 'recording' : 'paused'} />
          </div>

          <div className="px-4 py-3 max-h-72 overflow-y-auto space-y-3">
            {segments.length === 0 && !interimText && (
              <p className="text-sm text-[var(--text-ter)] italic">
                {isRecording
                  ? 'Listening… start speaking to capture the transcript.'
                  : 'Recording paused.'}
              </p>
            )}

            {segments.map((seg) => (
              <SegmentRow key={seg.id} seg={seg} />
            ))}

            {/* Live interim text */}
            {interimText && (
              <div className="flex items-start gap-3 opacity-60">
                <SpeakerAvatar label={userName} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <span className="text-xs font-medium text-[var(--text-sec)]">{userName}</span>
                    <span className="text-xs text-[var(--text-ter)] italic">typing…</span>
                  </div>
                  <p className="text-sm text-[var(--text-sec)] italic">{interimText}</p>
                </div>
              </div>
            )}

            <div ref={transcriptEndRef} />
          </div>
        </div>
      )}

      {/* ── Past recordings ── */}
      {isLoading && recordings.length === 0 && (
        <div className="py-8 text-center text-sm text-[var(--text-ter)]">
          Loading recordings…
        </div>
      )}

      {!isLoading && recordings.length === 0 && !hasActiveSession && (
        <div className="py-12 text-center">
          <MicOff className="h-10 w-10 text-[var(--text-ter)] mx-auto mb-3" />
          <p className="text-sm font-medium text-[var(--text-ter)]">No recordings yet</p>
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            Start a recording to capture the session discussion.
          </p>
        </div>
      )}

      {recordings.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-[var(--text-ter)]">
            Recordings
          </h3>

          {recordings.map((rec) => (
            <div key={rec.id}>
              <PastRecordingRow
                recording={rec}
                isExpanded={expandedIds.has(rec.id)}
                isActive={rec.id === activeRecordingId}
                onToggle={() => toggleExpanded(rec.id)}
                onSelect={() => handleSelectRecording(rec.id)}
              />

              {expandedIds.has(rec.id) && rec.id === selectedRecordingId && (
                <TranscriptBody
                  segments={segments}
                  status={rec.status}
                  isLive={rec.id === activeRecordingId}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Transcript body ──────────────────────────────────────────────────────────

interface TranscriptBodyProps {
  segments: import('../../../types/prioritization/sessionRecording').TranscriptSegment[];
  status: SessionRecording['status'];
  isLive: boolean;
}

function TranscriptBody({ segments, status, isLive: _isLive }: TranscriptBodyProps) {
  if (segments.length === 0) {
    return (
      <div className="border-x border-b border-[var(--rule)] px-4 py-6 text-center">
        <p className="text-sm text-[var(--text-ter)]">
          {status === 'recording' || status === 'paused'
            ? 'Transcript will appear here as participants speak.'
            : 'No transcript captured for this recording.'}
        </p>
      </div>
    );
  }

  return (
    <div className="border-x border-b border-[var(--rule)] px-4 py-3 max-h-80 overflow-y-auto space-y-3">
      {segments.map((seg) => (
        <SegmentRow key={seg.id} seg={seg} />
      ))}
    </div>
  );
}

// ─── Individual segment row ───────────────────────────────────────────────────

interface SegmentRowProps {
  seg: import('../../../types/prioritization/sessionRecording').TranscriptSegment;
}

function SegmentRow({ seg }: SegmentRowProps) {
  return (
    <div className="flex items-start gap-3">
      <SpeakerAvatar label={seg.speakerLabel} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <span className="text-xs font-medium text-[var(--text-sec)]">{seg.speakerLabel || 'Unknown'}</span>
          <span className="text-xs text-[var(--text-ter)]">{formatOffsetClock(seg.startMs)}</span>
        </div>
        <p className="text-sm text-[var(--text-pri)] leading-relaxed">{seg.text}</p>
      </div>
    </div>
  );
}

// ─── Speaker avatar ───────────────────────────────────────────────────────────

function SpeakerAvatar({ label }: { label: string }) {
  return (
    <div
      className={`flex-shrink-0 h-7 w-7 rounded-full flex items-center justify-center text-xs font-semibold ${speakerColor(label)}`}
    >
      {speakerInitials(label) || <User className="h-3.5 w-3.5" />}
    </div>
  );
}
